// src/pages/Vouchers/VoucherForm.jsx
// ======================================================================
// نموذج سند موحّد لكل الأنواع (قبض عميل / قبض عام / صرف مورد / صرف عام / صرف ضريبة)
// كل نوع يحدَّد بـ config من VOUCHER_CONFIGS — والملفات القديمة صارت أغلفة من 3 أسطر
// ======================================================================

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Alert, CircularProgress,
  Autocomplete, ToggleButton, ToggleButtonGroup, Switch, FormControlLabel, Divider,
  InputAdornment, Tooltip,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import TagIcon from '@mui/icons-material/Tag';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import axios from '../../axios';
import AccountSelect from '../../components/AccountSelect';
import { ReportPage, fieldSx, fmtMoney } from '../../components/Reports/ReportKit';
import { tafqeet } from '../../utils/tafqeet';
import VoucherPrint from './VoucherPrint';

// ───────────────────────── إعدادات الأنواع ─────────────────────────
// party: customer | supplier | account
const RECEIPT = { kind: 'receipt', accent: '#059669', soft: '#d1fae5', dirLabel: 'قبض — وارد للخزينة' };
const PAYMENT = { kind: 'payment', accent: '#e11d48', soft: '#ffe4e6', dirLabel: 'صرف — خارج من الخزينة' };

export const VOUCHER_CONFIGS = {
  customerReceipt: {
    ...RECEIPT, type: 101, title: 'سند قبض من عميل', icon: <PersonOutlineIcon />,
    party: 'customer', partyLabel: 'العميل', endpoint: '/Vouchers/customer-receipt',
  },
  generalReceipt: {
    ...RECEIPT, type: 103, title: 'سند قبض عام', icon: <ArrowCircleDownOutlinedIcon />,
    party: 'account', partyLabel: 'الحساب الدائن (الإيراد / أخرى)', endpoint: '/Vouchers/general-receipt',
  },
  supplierPayment: {
    ...PAYMENT, type: 102, title: 'سند صرف لمورد', icon: <LocalShippingOutlinedIcon />,
    party: 'supplier', partyLabel: 'المورد', endpoint: '/Vouchers/supplier-payment',
  },
  generalPayment: {
    ...PAYMENT, type: 104, title: 'سند صرف عام', icon: <ArrowCircleUpOutlinedIcon />,
    party: 'account', partyLabel: 'الحساب المدين (المصروف / أخرى)', endpoint: '/Vouchers/general-payment',
  },
  taxPayment: {
    ...PAYMENT, accent: '#d97706', soft: '#fef3c7', type: 105, title: 'سند صرف مع ضريبة',
    icon: <AccountBalanceOutlinedIcon />, party: 'supplier', partyLabel: 'المورد (الجهة الضريبية)',
    endpoint: '/Vouchers/Tax-payment', tax: true, defaultDescription: 'سند صرف ضريبة برقم',
  },
};

// ───────────────────────── أدوات مساعدة ─────────────────────────
const CASH = 1;
const BANK = 2;
const today = () => dayjs().format('YYYY-MM-DD');
const toDate = (v) => (v ? String(v).split('T')[0] : '');
const toApiDate = (d) => (d ? `${d}T12:00:00.000Z` : null); // الظهر لتفادي فرق المنطقة الزمنية
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const ratePercent = (rate) => { const r = Number(rate) || 0; return r <= 1 ? r * 100 : r; };

const readInt = (key) => { const v = parseInt(localStorage.getItem(key), 10); return Number.isFinite(v) ? v : null; };
const readIds = (key) => {
  try { const v = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(v) ? v : []; }
  catch { return []; }
};

// ───────────────────────── مكونات صغيرة ─────────────────────────
function Section({ title, icon, accent, children, action }) {
  return (
    <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: '14px', p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '9px', display: 'grid', placeItems: 'center',
          bgcolor: `${accent}1a`, color: accent, '& svg': { fontSize: 18 },
        }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, fontSize: 15, color: '#0f172a', flex: 1 }}>{title}</Typography>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

function Grid2({ children, cols = { xs: 1, sm: 2, md: 3 } }) {
  return (
    <Box sx={{
      display: 'grid', gap: 1.5,
      gridTemplateColumns: Object.fromEntries(Object.entries(cols).map(([k, n]) => [k, `repeat(${n}, minmax(0,1fr))`])),
    }}>{children}</Box>
  );
}

function SummaryRow({ label, value, strong, color }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, py: .6 }}>
      <Typography sx={{ fontSize: 13.5, color: '#64748b' }}>{label}</Typography>
      <Typography sx={{
        fontSize: strong ? 16 : 14, fontWeight: strong ? 800 : 600, color: color || '#0f172a',
        textAlign: 'left', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{value || '—'}</Typography>
    </Box>
  );
}

// ======================================================================
export default function VoucherForm({ config: cfg }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const isView = pathname.includes('/view');
  const isEdit = !!id && !isView;

  const companyId = readInt('companyId');
  const branchId = readInt('branchId');
  const userId = readInt('userId');
  const restrictedSafeId = readInt('safeId');
  const allowedSafeIds = useMemo(() => readIds('allowedSafeIds'), []);

  const [form, setForm] = useState(() => ({
    voucherNumber: '',
    voucherDate: today(),
    referenceNumber: '',
    referenceDate: today(),
    partyId: null,           // customerId / supplierId
    accountId: '',
    paymentType: CASH,       // 1 نقدي — 2 بنكي (نفس قيم الـ API)
    safeId: restrictedSafeId || (allowedSafeIds.length === 1 ? allowedSafeIds[0] : '') || '',
    bankId: '',
    amount: '',
    description: cfg.defaultDescription || '',
    vatTypeId: '',
    priceIncludesTax: false,
    journalEntryGlobalId: null,
    accountName: '',
  }));
  const [errors, setErrors] = useState({});
  const [parties, setParties] = useState([]);
  const [safes, setSafes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [vatTypes, setVatTypes] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const set = (name, value) => {
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(e => ({ ...e, [name]: undefined }));
  };

  // ── الصناديق المسموحة للمستخدم ──
  const allowedSafes = useMemo(() => safes.filter(s => {
    if (restrictedSafeId) return s.safeId === restrictedSafeId;
    if (allowedSafeIds.length > 0) return allowedSafeIds.includes(s.safeId);
    return true;
  }), [safes, restrictedSafeId, allowedSafeIds]);

  // ── تحميل البيانات ──
  useEffect(() => {
    if (!companyId) { setLoading(false); setLoadError('لم يتم تحديد الشركة — سجّل الدخول من جديد'); return; }
    let alive = true;

    (async () => {
      setLoading(true);
      const partyUrl = cfg.party === 'customer' ? `/Customer/by-company/${companyId}`
                     : cfg.party === 'supplier' ? `/Supplier/by-company/${companyId}` : null;

      const [safesR, banksR, partyR, vatR, companyR, numberR, voucherR] = await Promise.allSettled([
        axios.get(`/Safe/by-company/${companyId}`),
        axios.get(`/Bank/by-company/${companyId}`),
        partyUrl ? axios.get(partyUrl) : Promise.resolve({ data: [] }),
        cfg.tax ? axios.get('/VatTypes', { params: { companyId } }) : Promise.resolve({ data: [] }),
        axios.get(`/CompanyProfile/public/${companyId}`),
        !id && branchId ? axios.get(`/Vouchers/next-number/${companyId}/${branchId}/${cfg.type}`) : Promise.resolve(null),
        id ? axios.get(`/Vouchers/${id}`) : Promise.resolve(null),
      ]);
      if (!alive) return;

      const data = (r) => (r.status === 'fulfilled' && Array.isArray(r.value?.data) ? r.value.data : []);
      const safesList = data(safesR);
      const banksList = data(banksR);
      setSafes(safesList);
      setBanks(banksList);
      setParties(data(partyR));
      setVatTypes(data(vatR));
      if (companyR.status === 'fulfilled') setCompanyInfo(companyR.value?.data ?? null);

      if (id) {
        if (voucherR.status !== 'fulfilled') {
          setLoadError('تعذّر تحميل السند — ربما حُذف أو ليس لديك صلاحية');
        } else {
          const v = voucherR.value.data;
          setForm(f => ({
            ...f,
            voucherNumber: v.voucherNumber,
            voucherDate: toDate(v.voucherDate) || today(),
            referenceNumber: v.referenceNumber || '',
            referenceDate: toDate(v.referenceDate),
            partyId: v.customerId ?? v.supplierId ?? null,
            accountId: v.accountId ?? '',
            accountName: v.accountName || '',
            paymentType: v.paymentType === BANK ? BANK : CASH,
            safeId: v.safeId || '',
            bankId: v.bankId || '',
            amount: v.amount ?? '',
            description: v.description || '',
            vatTypeId: v.vatTypeId ?? '',
            priceIncludesTax: false, // المبلغ المحفوظ دائمًا قبل الضريبة
            journalEntryGlobalId: v.journalEntryGlobalId ?? null,
          }));
        }
      } else {
        const n = numberR.status === 'fulfilled' ? (numberR.value?.data?.nextNumber ?? numberR.value?.data) : null;
        // اختيار تلقائي إذا كان هناك صندوق/بنك واحد فقط
        const visibleSafes = safesList.filter(s =>
          restrictedSafeId ? s.safeId === restrictedSafeId
          : allowedSafeIds.length ? allowedSafeIds.includes(s.safeId) : true);
        setForm(f => ({
          ...f,
          voucherNumber: n != null ? String(n) : '',
          safeId: f.safeId || (visibleSafes.length === 1 ? visibleSafes[0].safeId : ''),
          bankId: f.bankId || (banksList.length === 1 ? banksList[0].id : ''),
        }));
      }
      setLoading(false);
    })();

    return () => { alive = false; };
  }, [companyId, branchId, id, cfg.type, cfg.party, cfg.tax, restrictedSafeId, allowedSafeIds]);

  // ── حساب الضريبة ──
  const vat = vatTypes.find(v => String(v.id) === String(form.vatTypeId));
  const calc = useMemo(() => {
    const amt = parseFloat(form.amount) || 0;
    if (!cfg.tax || !vat) return { net: amt, tax: 0, total: amt, rate: 0 };
    const r = ratePercent(vat.rate) / 100;
    if (form.priceIncludesTax) {
      const net = amt / (1 + r);
      return { net: round2(net), tax: round2(amt - net), total: round2(amt), rate: r * 100 };
    }
    return { net: round2(amt), tax: round2(amt * r), total: round2(amt * (1 + r)), rate: r * 100 };
  }, [form.amount, form.priceIncludesTax, vat, cfg.tax]);

  // ── أسماء للعرض والطباعة ──
  const party = parties.find(p => p.id === form.partyId) || null;
  const safe = safes.find(s => s.safeId === form.safeId);
  const bank = banks.find(b => b.id === form.bankId);
  const partyName = cfg.party === 'account' ? form.accountName : party?.name;

  const voucherForPrint = {
    voucherType: cfg.type,
    voucherNumber: form.voucherNumber,
    voucherDate: form.voucherDate,
    paymentType: form.paymentType,
    referenceNumber: form.referenceNumber,
    description: form.description,
    amount: calc.net,
    taxAmount: cfg.tax ? calc.tax : undefined,
    totalAmount: calc.total,
    vatName: vat?.name,
    customerName: cfg.party === 'customer' ? party?.name : undefined,
    supplierName: cfg.party === 'supplier' ? party?.name : undefined,
    accountName: cfg.party === 'account' ? form.accountName : undefined,
    safeName: form.paymentType === CASH ? safe?.name : undefined,
    bankName: form.paymentType === BANK ? bank?.name : undefined,
  };

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${cfg.title}-${form.voucherNumber || 'جديد'}`,
  });

  // ── التحقق ──
  const validate = () => {
    const e = {};
    if (!form.voucherDate) e.voucherDate = 'التاريخ مطلوب';
    if (cfg.party !== 'account' && !form.partyId) e.partyId = `اختر ${cfg.party === 'customer' ? 'العميل' : 'المورد'}`;
    if (cfg.party === 'account' && !form.accountId) e.accountId = 'اختر الحساب';
    if (!(parseFloat(form.amount) > 0)) e.amount = 'أدخل مبلغًا أكبر من صفر';
    if (form.paymentType === CASH && !form.safeId) e.safeId = 'اختر الصندوق';
    if (form.paymentType === BANK && !form.bankId) e.bankId = 'اختر البنك';
    if (cfg.tax && !form.vatTypeId) e.vatTypeId = 'اختر نوع الضريبة';
    setErrors(e);
    const first = Object.values(e)[0];
    if (first) toast.error(first);
    return !first;
  };

  // ── الحفظ ──
  const handleSubmit = async (ev) => {
    ev?.preventDefault();
    if (isView || submitting || !validate()) return;

    setSubmitting(true);
    const payload = {
      id: id ? parseInt(id, 10) : 0,
      companyId,
      branchId,
      voucherType: cfg.type,
      paymentType: form.paymentType,
      amount: cfg.tax ? calc.net : round2(form.amount),
      voucherDate: toApiDate(form.voucherDate),
      description: form.description || '',
      notes: form.description || '',
      safeId: form.paymentType === CASH ? Number(form.safeId) : null,
      bankId: form.paymentType === BANK ? Number(form.bankId) : null,
      userId,
      createdBy: localStorage.getItem('userName') || 'System',
      referenceNumber: form.referenceNumber || null,
      referenceDate: toApiDate(form.referenceDate),
      ...(cfg.party === 'customer' && { customerId: Number(form.partyId) }),
      ...(cfg.party === 'supplier' && { supplierId: Number(form.partyId) }),
      ...(cfg.party === 'account' && { accountId: form.accountId }),
      ...(cfg.tax && { isTax: 1, vatTypeId: Number(form.vatTypeId) }),
    };

    try {
      if (id) {
        await axios.put(`/Vouchers/${id}`, payload);
        toast.success(`تم تحديث ${cfg.title} بنجاح`);
      } else {
        await axios.post(cfg.endpoint, payload);
        toast.success(`تم حفظ ${cfg.title} بنجاح`);
      }
      navigate('/vouchers-list');
    } catch (err) {
      console.error('Voucher save error:', err);
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء حفظ السند');
    } finally {
      setSubmitting(false);
    }
  };

  // Ctrl+S للحفظ
  const submitRef = useRef(handleSubmit);
  submitRef.current = handleSubmit;
  const onKey = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); submitRef.current(); }
  }, []);
  useEffect(() => {
    if (isView) return undefined;
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isView, onKey]);

  // ── الترويسة ──
  const modeBadge = isView
    ? { label: 'عرض فقط', icon: <VisibilityOutlinedIcon /> }
    : isEdit ? { label: 'تعديل', icon: <EditOutlinedIcon /> }
    : { label: 'سند جديد', icon: <AddCircleOutlineIcon /> };

  const actions = [
    ...(form.journalEntryGlobalId ? [{
      label: 'القيد المحاسبي', icon: <MenuBookOutlinedIcon />,
      onClick: () => window.open(`/journal-entries/${form.journalEntryGlobalId}/view`, '_blank'),
    }] : []),
    { label: 'طباعة', icon: <PrintOutlinedIcon />, onClick: handlePrint, disabled: loading || !!loadError },
    { label: 'رجوع للقائمة', icon: <ArrowForwardIcon />, onClick: () => navigate('/vouchers-list') },
  ];

  const ro = isView || submitting;
  const accent = cfg.accent;

  return (
    <ReportPage
      title={cfg.title}
      subtitle={cfg.dirLabel}
      icon={cfg.icon}
      badges={[
        { label: form.voucherNumber ? `رقم ${form.voucherNumber}` : 'رقم تلقائي', icon: <TagIcon /> },
        modeBadge,
      ]}
      actions={actions}
    >
      {/* نسخة الطباعة (مخفية) */}
      <Box sx={{ display: 'none' }}>
        <VoucherPrint ref={printRef} voucher={voucherForPrint} companyInfo={companyInfo} />
      </Box>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 280 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert severity="error" sx={{ borderRadius: '12px' }}>{loadError}</Alert>
      ) : (
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            display: 'grid', gap: 1.5, alignItems: 'start',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 360px' },
          }}
        >
          {/* ═════ العمود الرئيسي ═════ */}
          <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
            <Section title="بيانات السند" icon={<EventNoteOutlinedIcon />} accent={accent}>
              <Grid2>
                <TextField
                  size="small" fullWidth type="date" label="تاريخ السند" required
                  value={form.voucherDate} onChange={e => set('voucherDate', e.target.value)}
                  InputLabelProps={{ shrink: true }} disabled={ro}
                  error={!!errors.voucherDate} helperText={errors.voucherDate}
                  sx={{ ...fieldSx, '& input': { direction: 'ltr', textAlign: 'right' } }}
                />
                <TextField
                  size="small" fullWidth label="رقم المرجع" placeholder="شيك / حوالة / فاتورة"
                  value={form.referenceNumber} onChange={e => set('referenceNumber', e.target.value)}
                  disabled={ro} sx={fieldSx}
                />
                <TextField
                  size="small" fullWidth type="date" label="تاريخ المرجع"
                  value={form.referenceDate} onChange={e => set('referenceDate', e.target.value)}
                  InputLabelProps={{ shrink: true }} disabled={ro}
                  sx={{ ...fieldSx, '& input': { direction: 'ltr', textAlign: 'right' } }}
                />
              </Grid2>
            </Section>

            <Section
              title={cfg.kind === 'receipt' ? 'الطرف وطريقة القبض' : 'الطرف وطريقة الصرف'}
              icon={<PaymentsOutlinedIcon />} accent={accent}
            >
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                {cfg.party === 'account' ? (
                  <AccountSelect
                    fullWidth size="small"
                    label={cfg.partyLabel}
                    value={form.accountId}
                    onChange={(val, acc) => {
                      set('accountId', val);
                      if (acc?.name || acc?.accountName) set('accountName', acc.name || acc.accountName);
                    }}
                    required
                    error={!!errors.accountId}
                    helperText={errors.accountId}
                    disabled={ro}
                  />
                ) : (
                  <Autocomplete
                    size="small"
                    options={parties}
                    value={party}
                    onChange={(_, v) => set('partyId', v ? v.id : null)}
                    getOptionLabel={o => o?.name || ''}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    disabled={ro}
                    noOptionsText="لا توجد نتائج"
                    renderOption={(props, o) => (
                      <li {...props} key={o.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                          <span>{o.name}</span>
                          {(o.phone || o.mobile) && (
                            <Typography component="span" sx={{ fontSize: 12, color: '#94a3b8', direction: 'ltr' }}>
                              {o.phone || o.mobile}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    renderInput={p => (
                      <TextField
                        {...p} label={cfg.partyLabel} required sx={fieldSx}
                        error={!!errors.partyId} helperText={errors.partyId}
                      />
                    )}
                  />
                )}

                <Box sx={{
                  display: 'grid', gap: 1.5, alignItems: 'start',
                  gridTemplateColumns: { xs: '1fr', sm: 'auto minmax(0,1fr)' },
                }}>
                  <ToggleButtonGroup
                    exclusive size="small" value={form.paymentType} disabled={ro}
                    onChange={(_, v) => v && set('paymentType', v)}
                    sx={{
                      height: 40,
                      '& .MuiToggleButton-root': {
                        px: 2, gap: .75, fontWeight: 700, fontSize: 14, borderRadius: '10px !important',
                        border: '1px solid #e5e7eb !important', mx: .25, flex: { xs: 1, sm: 'none' },
                      },
                      '& .Mui-selected': { bgcolor: `${accent}14 !important`, color: `${accent} !important`, borderColor: `${accent} !important` },
                    }}
                  >
                    <ToggleButton value={CASH}><PaymentsOutlinedIcon fontSize="small" /> نقدي</ToggleButton>
                    <ToggleButton value={BANK}><AccountBalanceOutlinedIcon fontSize="small" /> بنكي</ToggleButton>
                  </ToggleButtonGroup>

                  {form.paymentType === CASH ? (
                    <Autocomplete
                      size="small"
                      options={allowedSafes}
                      value={safe || null}
                      onChange={(_, v) => set('safeId', v ? v.safeId : '')}
                      getOptionLabel={o => o?.name || ''}
                      isOptionEqualToValue={(o, v) => o.safeId === v.safeId}
                      disabled={ro || !!restrictedSafeId}
                      noOptionsText="لا توجد صناديق مسموحة"
                      renderInput={p => (
                        <TextField {...p} label="الصندوق" required sx={fieldSx}
                          error={!!errors.safeId} helperText={errors.safeId} />
                      )}
                    />
                  ) : (
                    <Autocomplete
                      size="small"
                      options={banks}
                      value={bank || null}
                      onChange={(_, v) => set('bankId', v ? v.id : '')}
                      getOptionLabel={o => o?.name || ''}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      disabled={ro}
                      noOptionsText="لا توجد بنوك"
                      renderInput={p => (
                        <TextField {...p} label="البنك" required sx={fieldSx}
                          error={!!errors.bankId} helperText={errors.bankId} />
                      )}
                    />
                  )}
                </Box>
              </Box>
            </Section>

            {cfg.tax && (
              <Section title="الضريبة" icon={<PercentOutlinedIcon />} accent={accent}>
                <Box sx={{
                  display: 'grid', gap: 1.5, alignItems: 'center',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1fr) auto' },
                }}>
                  <TextField
                    select size="small" fullWidth label="نوع الضريبة" required
                    value={form.vatTypeId} onChange={e => set('vatTypeId', e.target.value)}
                    disabled={ro} sx={fieldSx}
                    error={!!errors.vatTypeId} helperText={errors.vatTypeId}
                  >
                    {vatTypes.map(vt => (
                      <MenuItem key={vt.id} value={vt.id}>
                        {vt.name} ({ratePercent(vt.rate).toFixed(2)}%)
                      </MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel
                    sx={{ m: 0, px: 1.25, height: 40, borderRadius: '10px', border: '1px solid #e5e7eb', bgcolor: '#f8fafc' }}
                    control={
                      <Switch
                        size="small" checked={form.priceIncludesTax} disabled={ro}
                        onChange={e => set('priceIncludesTax', e.target.checked)}
                      />
                    }
                    label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>المبلغ شامل الضريبة</Typography>}
                  />
                </Box>
              </Section>
            )}

            <Section title="البيان / الملاحظات" icon={<NotesOutlinedIcon />} accent={accent}>
              <TextField
                fullWidth multiline minRows={3} maxRows={8}
                placeholder="وذلك عن…"
                value={form.description} onChange={e => set('description', e.target.value)}
                disabled={ro} sx={fieldSx}
              />
            </Section>
          </Box>

          {/* ═════ ملخّص السند ═════ */}
          <Paper elevation={0} sx={{
            position: { lg: 'sticky' }, top: { lg: 16 },
            border: '1px solid #e5e7eb', borderRadius: '16px', overflow: 'hidden',
            boxShadow: '0 12px 30px -20px rgba(15,23,42,.35)',
          }}>
            <Box sx={{ height: 5, bgcolor: accent }} />
            <Box sx={{ p: { xs: 1.75, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 15 }}>ملخّص السند</Typography>
                <Box sx={{
                  px: 1.25, py: .35, borderRadius: '999px', fontSize: 12.5, fontWeight: 700,
                  bgcolor: cfg.soft, color: accent,
                }}>
                  {cfg.kind === 'receipt' ? 'قبض ↓' : 'صرف ↑'}
                </Box>
              </Box>

              <TextField
                fullWidth label={cfg.tax && form.priceIncludesTax ? 'المبلغ شامل الضريبة' : 'المبلغ'}
                type="number" required
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
                onWheel={e => e.target.blur()}
                disabled={ro}
                error={!!errors.amount} helperText={errors.amount}
                inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
                InputProps={{ endAdornment: <InputAdornment position="end">ر.س</InputAdornment> }}
                sx={{
                  ...fieldSx,
                  '& input': { fontSize: 26, fontWeight: 800, color: accent, direction: 'ltr', textAlign: 'right', py: 1.25 },
                }}
              />

              {calc.total > 0 && (
                <Typography sx={{
                  mt: 1, p: 1, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px dashed #cbd5e1',
                  fontSize: 13, color: '#334155', lineHeight: 1.7,
                }}>
                  {tafqeet(calc.total)}
                </Typography>
              )}

              <Divider sx={{ my: 1.5 }} />

              {cfg.tax && (
                <>
                  <SummaryRow label="المبلغ قبل الضريبة" value={fmtMoney(calc.net)} />
                  <SummaryRow label={`الضريبة ${calc.rate ? `(${calc.rate.toFixed(0)}%)` : ''}`} value={fmtMoney(calc.tax)} />
                  <SummaryRow label="الإجمالي المدفوع" value={fmtMoney(calc.total)} strong color={accent} />
                  <Divider sx={{ my: 1 }} />
                </>
              )}

              <SummaryRow label={cfg.kind === 'receipt' ? 'استلمنا من' : 'صرفنا إلى'} value={partyName} />
              <SummaryRow
                label={form.paymentType === CASH ? 'من الصندوق' : 'عبر البنك'}
                value={form.paymentType === CASH ? safe?.name : bank?.name}
              />
              <SummaryRow label="التاريخ" value={form.voucherDate && dayjs(form.voucherDate).format('DD/MM/YYYY')} />

              {!isView ? (
                <Box sx={{ display: 'grid', gap: 1, mt: 2 }}>
                  <Button
                    type="submit" variant="contained" size="large" disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
                    sx={{
                      borderRadius: '12px', fontWeight: 800, fontSize: 15, py: 1.1,
                      bgcolor: accent, '&:hover': { bgcolor: accent, filter: 'brightness(.92)' },
                      '& .MuiButton-startIcon': { ml: 1, mr: -.5 },
                    }}
                  >
                    {submitting ? 'جاري الحفظ…' : id ? 'تحديث السند' : 'حفظ السند'}
                  </Button>
                  <Button
                    variant="outlined" color="inherit" disabled={submitting}
                    onClick={() => navigate('/vouchers-list')}
                    sx={{ borderRadius: '12px', fontWeight: 700, borderColor: '#e5e7eb' }}
                  >
                    إلغاء
                  </Button>
                  <Tooltip title="اختصار لوحة المفاتيح">
                    <Typography sx={{ fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>Ctrl + S للحفظ</Typography>
                  </Tooltip>
                </Box>
              ) : (
                <Button
                  fullWidth variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={handlePrint}
                  sx={{ mt: 2, borderRadius: '12px', fontWeight: 700, '& .MuiButton-startIcon': { ml: 1, mr: -.5 } }}
                >
                  طباعة السند
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </ReportPage>
  );
}
